import { Dayjs } from 'dayjs';
import type { EmailDomainSuggestion } from './emailDomainSuggestion.ts';

export interface AuthResponse {
  user: string;
  new: boolean;
  token: string;
  is_concierge_member?: boolean;
}

export interface PostApiV20Verify {
  phone: string;
  channel: string | null;
  'g-recaptcha-response'?: string;
}

export interface PostApiV20Verification {
  phone: string;
  code: string;
  'g-recaptcha-response'?: string;
  subscribe_to_general_marketing_sms?: boolean;
}

interface PaymentMethodOption {
  mandate_options: null;
  network: null;
  request_three_d_secure: 'automatic';
}

interface PaymentMethodOptions {
  card: PaymentMethodOption;
}

interface SetupIntent {
  id: string;
  object: string;
  application: string | null;
  automatic_payment_methods: string | null;
  cancellation_reason: string | null;
  client_secret: string;
  created: number;
  customer: string;
  description: string | null;
  flow_directions: string | null;
  last_setup_error: string | null;
  latest_attempt: string | null;
  livemode: boolean;
  mandate: string | null;
  metadata: string[];
  next_action: string | null;
  on_behalf_of: string | null;
  payment_method: string | null;
  payment_method_options: PaymentMethodOptions;
  payment_method_types: string[];
  single_use_mandate: string | null;
  status: string;
  usage: string;
}

export interface SetupIntentResponse {
  setupIntent: SetupIntent;
}

export interface SetPaymentMethodResponse {
  payment_id: string;
}

interface ConfirmationResponseSeating {
  id: number;
  price: number;
  seats: number;
  min_seats: number;
}

interface ConfirmationResponseRestaurant {
  address: string;
  area_id: number;
  area_name: string;
  cuisine_id: number;
  fun_fact: string | null;
  has_frictionless: boolean;
  cancellation_policy: string;
  id: number;
  latitude: string;
  launching_at: string;
  long_description: string | null;
  longitude: string;
  max_size: number;
  medium_description: string;
  meta_contacts: null;
  min_size: number;
  name: string;
  need_to_knows: NeedToKnow[];
  neighborhood_name: string;
  operating_hours: OperatingHour[][];
  phone: string;
  pos_system: null;
  price_starting_at__currency: string;
  price_starting_at: number;
  primary_photo: string;
  required_tag_id: null;
  reservation_system: null;
  reservations_open_at: string | null;
  short_description: string;
  special_request_days: {
    sun: boolean;
    mon: boolean;
    tue: boolean;
    wed: boolean;
    thu: boolean;
    fri: boolean;
    sat: boolean;
  };
  squarish_crop_image_url: string;
  tall_crop_image_url: string;
  thumbnail_crop_image_url: string;
  timezone: string;
  transparent_logo_image_url: string | null;
  trending: number;
  type: string;
  website: string;
  welcome_id: string | null;
  wide_crop_image_monochrome_url: string;
  wide_crop_image_url: string;
  show_merchandising_tax_and_tip: boolean;
  total_prepayment_amount__currency: string;
  show_merchandising_price_totals: boolean;
  merchandising_policy_title: string;
  merchandising_policy: string;
  merchandising_policy_opt_in_label: string;
  merchandising_marketing_opt_in_label: string;
  expanded_tc: string | null;
  expanded_tc_title: string | null;
  expanded_tc_opt_in_label: string | null;
}

export interface ConfirmationCard {
  nickname: string | null;
  brand: string;
  number: string;
}

export interface ConfirmationResponseReservation {
  average_response_time_in_minutes: number;
  average_response_time_text: string | null;
  cancellation_policies: string[];
  cancellation_policy: string;
  card: ConfirmationCard;
  credits_used__currency: string;
  credits_used: number;
  points_used_currency_value__currency: string;
  points_used: number;
  date_full: string;
  date: string;
  fee_restaurant__currency: string;
  fee_restaurant: number;
  notices: string[];
  party: number;
  price_per_person__currency: string;
  price_per_person: number;
  remaining_credit: number;
  restaurant: ConfirmationResponseRestaurant;
  seating: ConfirmationResponseSeating;
  service_charges__currency: [ServiceChargesCurrency];
  service_charges: [ServiceCharges];
  table_type: null;
  tax_amount__currency: string;
  tax_amount: number;
  tax_rate: number;
  time: string;
  total__currency: string;
  total: number;
  type: string;
  window: string | null;
  total_prepayment_amount__currency: string;
}

export interface ConfirmationResponse {
  reservation: ConfirmationResponseReservation;
}

export interface RestaurantResponseRestaurant extends MappableRestaurant {
  address: string;
  area_id: number;
  area_name: string;
  average_response_time_in_minutes: number;
  average_response_time_text: string | null;
  currency_symbol: string;
  has_frictionless: boolean;
  has_dorsia_pay_lite: boolean;
  id: number;
  medium_description: string;
  merchandising_marketing_opt_in_label: boolean;
  merchandising_policy: string | null;
  merchandising_policy_title: string | null;
  merchandising_policy_opt_in_label: string | null;
  merchandising_unit_label: string | null;
  merchandising_unit_label_plural: string | null;
  name: string;
  need_to_knows: NeedToKnow[];
  neighborhood_name: string;
  phone: string;
  primary_photo: string | null;
  short_description: string;
  show_merchandising_price_totals: boolean;
  show_merchandising_tax_and_tip: boolean;
  squarish_crop_image_url: string;
  thumbnail_crop_image_url: string;
  timezone: string;
  type: string;
  website: string | null;
  wide_crop_image_url: string;
}
export interface ReservationResponseReservation {
  ach_payment_status?: string | null;
  cancellation_policy?: string | null;
  card: string | null;
  change_proposal: string | null;
  confirmation_code: string | null;
  confirmed_at: string | null;
  credits_used__currency: string;
  credits_used: number;
  date: string | null;
  failed_at: string | null;
  guests: string[];
  is_past: boolean;
  party_size: number;
  pending_modification_reservation: string | null;
  price_per_person__currency: string;
  price_per_person: number;
  proposed_time_frame: string | null;
  receipt: string | null;
  reservation_at: string;
  reservation_date_pretty: string;
  reservation_date_with_day_of_week: string;
  reservation_short_date_with_day_of_week: string;
  reservation_time_pretty: string;
  restaurant: RestaurantResponseRestaurant;
  service_charges__currency: [ServiceChargesCurrency];
  service_charges: [ServiceCharges];
  table_minimum__currency: string;
  table_minimum: number;
  table_type: string | null;
  tax_amount__currency: string;
  tax_amount: number;
  tax_rate: number;
  total_paid__currency: string;
  total_paid: number;
  total_prepayment_amount__currency: string;
  total_prepayment_amount: number;
  user: ResponseUser;
  uuid: string | null;
  alternative_pickup_contact?: AlternativePickupContact | null;
}

export interface CancellationResponseReservation extends ReservationResponseReservation {
  footnote: null;
  has_error: boolean;
}

export interface ReservationResponse {
  status: string;
  reservation: ReservationResponseReservation;
}

/** Public widget cancel-choice (HOS-3118): gifted access vs card refund minus fee. */
export type WidgetCancelChoice = 'standard' | 'gift' | 'refund';

export interface Post_Cancel {
  type: string;
  choice?: WidgetCancelChoice;
}

export interface Post_PaymentMethod {
  paymentMethodId: string;
}

export interface Post_Confirm {
  restaurant_id: number;
  seating_id: number;
  party: number;
  date: string;
  time: string;
  skip_level_check?: boolean;
  use_credits?: boolean;
}

export interface Post_Reserve {
  restaurant: number;
  seating: number;
  seats: number;
  date: string;
  confirmed_price: number;
  payment_method_id?: string;
  use_credits?: boolean;
  skip_level_check?: boolean;
  comments?: string;
  referral_code?: string;
  /**
   * Specific time chosen within a flex slot's window (H:i:s). Required by
   * the api's calendar booking path for any slot whose `slot_time_end` is
   * set; ignored on single-time slots. Sending it on every reservation is
   * safe — the api drops it when not needed and rejects the booking with
   * a 422 ("seating no longer available") when it's missing on a flex slot.
   */
  time?: string;
}

export interface EventReservationPayload {
  hosted_club_checkout?: boolean;
  idempotency_key?: string;
  items?: number[];
  confirmed_total?: number;
  payment_method_id?: string;
  use_credits?: boolean;
  skip_level_check?: boolean;
  opt_in_accepted?: number;
  alternative_pickup_name?: string;
  alternative_pickup_phone?: string;
  alternative_pickup_email?: string;
  payment_id?: string;
  referral_code?: string;
}

export interface EventDateTimeFormatted {
  date?: string;
  date_long?: string;
  date_dow_long?: string;
  date_dow_short?: string;
  time?: string;
}

export interface EventReservationEvent {
  id?: number;
  name?: string;
  /** Bookings are plain reservations with no tickets (EVNT-1818). */
  non_ticketed?: boolean;
  date?: string;
  time?: string;
  datetime_local__formatted?: EventDateTimeFormatted;
  card_image?: {
    id?: number;
    original_url?: string;
  };
}

export interface EventReservationData {
  requires_pmc_checkout?: boolean;
  event?: EventReservationEvent;
  restaurant?: RestaurantResponseData;
  user?: ResponseUser;
  items?: Array<{
    guests?: number;
    price__currency?: string;
    quantity?: number;
    item?: {
      name?: string;
      image?: string;
      description?: string;
    };
  }>;
  party_size?: number;
  fee_restaurant?: number;
  fee_restaurant__currency?: string;
  service_charges?: ServiceCharges[];
  service_charges__currency?: ServiceChargesCurrency[];
  tax_amount?: number;
  tax_amount__currency?: string;
  tax_rate?: number;
  total_paid?: number;
  total_paid__currency?: string;
  points_used_value?: number;
  credits_used?: number;
  credits_used__currency?: string;
  alternative_pickup_contact?: AlternativePickupContact | null;
  payment_intent_client_secret?: string;
  [key: string]: unknown;
}

export interface EventReservationResponse {
  checkout_url?: string;
  reservation_uuid?: string;
  status?: string;
  data?: EventReservationData;
}

export interface Post_Claim {
  payment_method_id: string;
  use_credits?: boolean;
  comments?: string;
}

export type Post_Tip_Type = 'percentage' | 'custom';

export interface Post_Tip {
  tip_amount: number;
  percentage: boolean;
  custom: boolean;
}

export interface Post_AdditionalTip {
  amount: number;
  percentage: boolean;
}

export interface Post_UpdateUser {
  first_name: string;
  last_name: string;
  email: string;
  birthday?: string;
  used_referral_code?: string;
}

interface Cuisine {
  id: number;
  name: string;
}

interface OperatingHour {
  start: string;
  end: string;
}

export interface NeedToKnow {
  icon: string;
  description: string;
}

interface Availability {
  date: string;
  amount_of_tables: number;
  amount_of_tables_label: string;
  min: number;
  max: number;
}

export interface AvailDate {
  formatted: string;
  date: string;
  amount_of_tables: number;
  amount_of_tables_label: string;
  allow_special_request: boolean;
}

export interface AvailabilityOption {
  diff_key?: string; // derived
  seats: number;
  price: number;
  price__currency: string;
  type: string | null; // table type name (e.g. "Yellow Zone"); null for untyped seatings
  seating_id: number;
  allowed_payment_types?: PaymentType[];
}

export interface MetaAvailability {
  uid: string;
  price: number;
  price__currency: string;
  label: string;
  datetime_label: string;
  date: string;
  time: string;
  datetime_utc: string;
  options: AvailabilityOption[];
  min_seats?: number; // derived
  max_seats?: number; // derived
}

export interface MappableRestaurant {
  latitude: number | string;
  longitude: number | string;
}

export interface AlternativePickupContact {
  name: string;
  phone: string;
  email: string;
}

export interface RestaurantResponseData extends MappableRestaurant {
  id: number;
  name: string;
  cuisine: Cuisine[];
  phone: string;
  website: string | null;
  address: string;
  area_id: number;
  launching_at: string; // date
  is_new_restaurant: boolean;
  reservations_open_at: string | null;
  neighborhood_name: string;
  short_description: string;
  medium_description: string;
  long_description: string | null;
  timezone: string;
  currency_symbol: string;
  operating_hours: OperatingHour[][];
  price_starting_at: number;
  price_starting_at__currency: string;
  wide_crop_image_url: string;
  tall_crop_image_url: string;
  transparent_logo_image_url?: string | null;
  venue_map_url?: string | null;
  special_request_days: {
    sun: boolean;
    mon: boolean;
    tue: boolean;
    wed: boolean;
    thu: boolean;
    fri: boolean;
    sat: boolean;
  };
  need_to_knows: NeedToKnow[];
  has_frictionless: boolean;
  min_size: number;
  max_size: number;
  next_available: Availability;
  type: string;
  allow_alternative_pickup?: boolean;
  show_merchandising_price_totals?: boolean;
  show_merchandising_tax_and_tip?: boolean;
  merchandising_policy?: string | null;
  merchandising_policy_title?: string | null;
  merchandising_policy_opt_in_label?: string | null;
  merchandising_marketing_opt_in_label?: string;
  expanded_tc?: string | null;
  expanded_tc_title?: string | null;
  expanded_tc_opt_in_label?: string | null;
  allowed_payment_types?: PaymentType[];
  allow_zero_dollar_booking_links_without_payment_method?: boolean;
  features?: {
    WidgetSmsOptIn?: boolean;
    WidgetCustomRequest?: boolean;
    WidgetCancelReservation?: boolean;
    WidgetCancelCreditGiftRefund?: boolean;
    /** True hides the food-and-drink caption under Minimum spend. Defaults off (caption shows). */
    HideMinimumSpendExplanation?: boolean;
  };
  /** Generated cancellation policy description (e.g. "Cancel up to 48 hours before…"). */
  cancellation_policy?: string | null;
  disclosure_text?: string | null;
  disclosure_link_url?: string | null;
  disclosure_link_label?: string | null;
}

/** Picks the best available image URL from a restaurant-like object. */
export function getRestaurantImage(
  restaurant?: {
    squarish_crop_image_url?: string;
    wide_crop_image_url?: string;
    thumbnail_crop_image_url?: string;
    primary_photo?: string | null;
  } | null
): string | undefined {
  if (!restaurant) return undefined;
  return (
    restaurant.squarish_crop_image_url ||
    restaurant.wide_crop_image_url ||
    restaurant.thumbnail_crop_image_url ||
    restaurant.primary_photo ||
    undefined
  );
}

export interface RestaurantResponseMeta {
  availability: MetaAvailability[];
  date: string;
  dates: AvailDate[];
  request_availability: [];
  seating: null;
  time: null;
}

export interface RestaurantResponse {
  data: RestaurantResponseData;
  meta: RestaurantResponseMeta;
}

export interface ResponseUser {
  active_user_communications: string[];
  application_status: string;
  approved_at: string;
  area_id: string | null;
  areas: number[];
  average_dining_week: string;
  birthday: string | null;
  contact_count: number | null;
  city: string | null;
  email: string;
  features: {
    MobileReservationTimeFrame: boolean;
    MobileRestaurantSorting: boolean;
    MobileCuisineSelection: boolean;
    MobileNeighborhoodFilter: boolean;
    MobileSplitPay: boolean;
  };
  first_name: string;
  gender: string | null;
  guest_pass_active: boolean;
  guest_pass_expires_at: string | null;
  instagram: string | null;
  job_title: string | null;
  last_name: string;
  linkedin: string | null;
  locale: string;
  member_reference: string | null;
  member_references: string | null;
  moment_formats: {
    date_long: string;
    date_short: string;
    date_dow_long: string;
    date_dow_short: string;
    date_slashes: string;
    date_slashes_short: string;
    time: string;
  };
  new_nominations: number;
  nomination_request_statement: string | null;
  nominations_allowed: number;
  nominations_remaining: number;
  nominations_requested: string | null;
  nominator_names: [];
  partnerships: [];
  phone: string;
  pm_exp_month: number;
  pm_exp_year: string;
  pm_last_four: string;
  pm_source: string;
  pm_type: string;
  primary_categorization: string | null;
  referral_code_active: boolean;
  saves: [];
  status: string;
  sufficient_contacts: boolean;
  twitter: string | null;
  unacknowledged_partnerships: [];
  uuid: string;
}

export interface GetReservation {
  ach_payment_status?: string | null;
  cancellation_policy: string | null;
  cancelled: boolean;
  card: ConfirmationCard;
  change_proposal: string | null;
  claim_deadline_at_utc: Dayjs;
  claim_deadline_at: string;
  claimed_at: string | null;
  confirmation_code: string;
  confirmation_deadline_at: string | null;
  confirmed_at: string;
  credits_used__currency: string;
  credits_used: number;
  fee_restaurant: number;
  fee_restaurant__currency: string;
  points_used: number;
  date: string;
  denied_at: string | null;
  failed_at: string | null;
  guests: string[];
  hash_signature: string | null;
  is_past: boolean;
  party_size: number;
  pending_modification_reservation: string | null;
  points_used_currency_value__currency: string | null;
  price_per_person__currency: string;
  price_per_person: number;
  proposed_time_frame: string | null;
  receipt: string | null;
  reservation_at_tzd: Dayjs; // derived
  reservation_at: string;
  reservation_date_pretty: string;
  reservation_date_with_day_of_week: string;
  reservation_short_date_with_day_of_week: string;
  reservation_time_pretty: string;
  restaurant: RestaurantResponseRestaurant;
  service_charges__currency: [ServiceChargesCurrency];
  service_charges: [ServiceCharges];
  sold_out: boolean;
  table_minimum__currency: string;
  table_minimum: number;
  table_type: string;
  tax_amount__currency: string;
  tax_amount: number;
  tax_rate: number;
  total_paid__currency: string;
  total_paid: number;
  total_prepayment_amount__currency: string;
  total_prepayment_amount: number;
  user: ResponseUser;
  uuid: string;
  seating_id: number;
  allowedPaymentTypes?: PaymentType[];
}

export interface GetReservationResponse {
  reservation: GetReservation;
}

export interface GetUserResponse {
  // two difference response formats
  status?: boolean; // option a
  finalized_registration?: boolean; // option a
  message?: string; // option b
  unacknowledged_partnerships?: []; // option b
  user: ResponseUser; // this was the same for both
}

export interface UpdateUserResponse {
  status: boolean;
  finalized_registration: boolean;
  user: ResponseUser;
  birthday?: string;
}

export interface UpdateUserErrorResponse {
  message: string;
  errors: {
    email: string[];
  };
  /**
   * Present only when the `email_domain` rule rejected the address AND a
   * suggestion was found [GRO-6465]. Absent (not `null`) otherwise.
   */
  email_domain?: EmailDomainSuggestion;
}

export interface GetUserCreditsResponse {
  data: UserCreditsResponse;
}

export interface UserCreditsResponse {
  credits?: CurrencyCreditsResponse;
  points?: number;
}

export interface CurrencyCreditsResponse {
  USD?: string;
  GBP?: string;
  AED?: string;
  EUR?: string;
  MXN?: string;
}

export interface UnfinishedUserProfileErrorResponse {
  message: 'Please tell us a little about yourself.';
  user: ResponseUser;
  unacknowledged_partnerships: string[];
}

export interface UnauthorizedErrorResponse {
  message: 'This action is unauthorized.';
}

export interface ReservationReceiptNotReady {
  message: "Reservation doesn't have an order created";
}

export interface AlreadyCanceledReservationErrorResponse {
  message: string;
}

export interface ServerErrorResponse {
  message: 'Server Error';
}

export interface ReserveErrorResponse {
  type: string;
  message: string;
}

export interface ReservationUnavailableErrorResponse {
  message: string;
}

export interface ProcessingPaymentErrorResponse {
  message: string;
}

export interface ReservationResourceNotFound {
  message: 'Resource not found';
}

export interface CardErrorResponse {
  // Stripe's member-actionable decline message (e.g. "Your card has expired."),
  // tagged type: 'card_error' by the API [HOS-2029]
  message: string;
  type: 'card_error';
}

// Pre-HOS-2029 API body for the same failure: fixed message, no discriminator.
// Delete once every API environment emits type: 'card_error'.
export interface LegacyCardErrorResponse {
  message: 'There was an error processing your payment. Please verify your card information or use another card.';
}

export interface ActionRequiredResponse {
  message: 'Payment processing requires action.';
  reservation_uuid: string;
  payment_id: string;
  payment_intent_client_secret: string;
  stripe_public_key: string;
}

export interface PaymentType {
  id: string;
  name: string;
  slug: string;
}

export interface PaymentMethod extends ConfirmationCard {
  id: string;
  expiration_date: string;
  default: boolean;
  postal_code: string;
}

export interface PaymentMethodsResponse {
  data: PaymentMethod[];
}

/** HOS-3118: whether this booking gets the gift-vs-card-refund choice + refund breakdown. */
export interface WidgetCancelChoiceInfo {
  eligible: boolean;
  fee_percent: number; // processing fee, e.g. 5
  cancellation_fee_percent: number; // policy penalty, e.g. 50 for a late cancel
  card_charge_total: number; // cents, full card charge
  card_charge_total__currency: string;
  cancellation_fee: number; // cents, policy penalty on the card charge
  cancellation_fee__currency: string;
  card_refund_fee: number; // cents, processing fee on the eligible amount
  card_refund_fee__currency: string;
  card_refund_after_fee: number; // cents, net refund to card
  card_refund_after_fee__currency: string;
  credits_back: number; // cents, credits returned on the credits/gift path
  credits_back__currency: string;
  // On the card-refund path, the credits and Fun Coupons the booking used still
  // go back to the wallet (policy share); they are never refunded to the card.
  refund_credits_back: number; // cents
  refund_credits_back__currency: string;
  refund_fun_coupons_back: number; // cents (value)
  refund_fun_coupons_back__currency: string;
  used_credits: boolean;
  used_fun_coupons: boolean;
}

/** What the guest got back, shown on the canceled screen. */
export interface CancelRefundSummary {
  card_refund: number; // cents, refunded to the card (after the processing fee)
  card_refund__currency: string;
  card_brand: string | null;
  card_last_four: string | null;
  credits: number; // cents, booking credits returned to the wallet
  credits__currency: string;
  fun_coupons: number; // cents (value), Fun Coupons returned to the wallet
  fun_coupons__currency: string;
}

export interface CancelConfirmResponse {
  reservation: CancellationResponseReservation;
  details: string[];
  credit: number;
  forfeit: number;
  type: 'standard';
  widget_cancel_choice?: WidgetCancelChoiceInfo;
}

export interface CancelFinishResponse {
  reservation: CancellationResponseReservation;
  charge: number;
  credit: number;
  choice?: WidgetCancelChoice;
  card_refund_after_fee?: number; // cents
  card_refund_after_fee__currency?: string;
  refund_summary?: CancelRefundSummary;
}

export interface TipOption {
  id: number;
  amount: number;
  label: string;
  percentage: boolean;
}

export interface ReservationReceiptRestaurant extends RestaurantResponseRestaurant {
  items_allowed: boolean;
  tip_allowed: boolean;
  tip_options: TipOption[];
}

interface AdditionalTip {
  amount: number;
  amount__currency: string;
  percentage: boolean;
  value: number;
  value__currency: string | null;
}

export interface ReceiptItem {
  name: string;
  price: number;
  voided: boolean;
  quantity: number;
  created_at: string;
  modified_at: string;
  price__currency: string;
}

export interface ReceiptTotal {
  label: string;
  amount: string;
}

type ReceiptStatus =
  | 'Opened' // soon
  | 'In-Order' // now
  | 'Finalized' // final
  | 'Payment-Failed'; // failed

export interface Receipt {
  external_tab_id: string;
  last_updated_from_integration: string;
  guests: number;
  total: number;
  total__currency: string;
  table_minimum_spend: number;
  table_minimum_spend__currency: string;
  balance: number;
  balance__currency: string;
  custom_tip: number | null;
  additional_tip: AdditionalTip;
  payment_id: string | null;
  last_4: string;
  closed_at: string | null;
  payment_type: string;
  status: ReceiptStatus;
  sub_total: number;
  sub_total__currency: string;
  tax: number;
  tax__currency: string;
  tip_percentage: number;
  tip_total: number;
  tip_total__currency: string;
  total_charged: number;
  total_charged__currency: string;
  tip_included_rate: number;
  tip_included: number;
  included_tip_amount: number;
  included_tip_amount__currency: string | null;
  reached_sub_total: number;
  reached_sub_total__currency: string;
  display_total: number;
  display_total__currency: string;
  tax_total: number;
  tax_total__currency: string;
  is_dorsia_payment: boolean;
  items: ReceiptItem[];
  card: ConfirmationCard;
  service_charges: string[]; // ???
  messages: string[]; // ???
  item_messages: string[];
  subtotals: ReceiptTotal[];
  totals: ReceiptTotal[];
  alt_prepayment: string | null;
}

interface SplitPayGuest {
  name: string;
  paying_for: number;
  amount: string;
  failed: boolean;
  is_host: boolean;
}

export interface SplitPay {
  guests: SplitPayGuest[];
  credit_amount: string | null;
  debit_amount: string | null;
}

export interface ReservationReceipt {
  reservation_at: string;
  confirmed_at: string;
  proposed_time_frame: string | null;
  restaurant: ReservationReceiptRestaurant;
  party_size: number;
  confirmation_code: string;
  total_paid: number;
  total_paid__currency: string;
  table_minimum: number;
  table_minimum__currency: string;
  total_prepayment_amount: number;
  total_prepayment_amount__currency: string;
  table_type: string;
  credits_used: number;
  price_per_person: number;
  price_per_person__currency: string;
  uuid: string;
  is_past: boolean;
  reservation_date_pretty: string;
  reservation_time_pretty: string;
  reservation_date_with_day_of_week: string;
  reservation_short_date_with_day_of_week: string;
  guests: string[];
  receipt: Receipt;
  pending_modification_reservation: string | null;
  change_proposal: string | null;
  card: ConfirmationCard;
  split_pay: SplitPay;
  footnote: string | null;
  has_error: boolean;
  payment_intent_id: string | null;
  alt_prepayment: string | null;
  tax_rate: number;
  tax_amount: number;
  tax_amount__currency: string;
  service_charges: [ServiceCharges];
  service_charges__crrency: [ServiceChargesCurrency];
}

export interface ServiceCharges {
  label: string;
  amount: number;
  amount__currency: string;
}

export interface ServiceChargesCurrency {
  label: string;
  amount: string;
}

export interface ReservationReceiptResponse {
  data: ReservationReceipt;
}

export interface ReservationPreview {
  confirmed_at: string;
  is_past: boolean;
  party_size: number;
  proposed_time_frame: string | null;
  reservation_at: string;
  reservation_date_pretty: string;
  reservation_date_with_day_of_week: string;
  reservation_short_date_with_day_of_week: string;
  reservation_time_pretty: string;
  restaurant: ReservationReceiptRestaurant;
  table_minimum__currency: string;
  table_minimum: number;
  table_type: string;
  uuid: string;
}

export interface ReservationPreviewResponse {
  data: ReservationPreview;
}
